import {
  FORMELEMENTS_ACCEPT_BUTTON,
  FORMELEMENTS_PARAGRAPH,
  FORMELEMENTS_SIGNATURE
} from '../components/FormBuilder/FormElements';
import {
  isFormItemNotEditable,
  applyFormConditionsAndDefaultValuesToForms,
  applyValuesInheritanceToForms,
  collectFormStructures,
  prepareDocumentsForCaseConditions
} from '../components/helpers/formUtils';
import { arrLightCopy } from '../components/helpers/immutable';
import {
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_FORM,
  DOC_TYPE_ID_CHECK,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  DOC_TYPE_PDF_FORM,
  isDocumentReady,
  isDocumentRejected,
  isDocumentSubmitted,
  isNotEmptyArray,
  prepareIdCheckSteps,
  shallowEqual
} from '../components/helpers/utils';
import { CaseConditionalLogic } from '@eqx/paperfly-conditional-lib';
import { DEMO_QES_APPEARANCE_DATA_URL } from '../components/formsCommon/esignatures/demoQesAppearanceDataUrl';

export const maxPagesCount = 500;

export function isIterable(obj) {
  // checks for null and undefined
  if (obj == null) {
    return false;
  }
  return obj instanceof Array;
}

export function create_UUID(format) {
  let dt = new Date().getTime();
  const template = format ? format : 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx';

  return template.replace(/[xy]/g, function(c) {
    let r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const nl2br = (str, is_xhtml) => {
  if (typeof str === 'undefined' || str === null) {
    return '';
  }
  const breakTag =
    is_xhtml || typeof is_xhtml === 'undefined' ? '<br />' : '<br>';
  return (str + '').replace(
    /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
    '$1' + breakTag + '$2'
  );
};

export function defineCurrentRecipientRoles(recipientId, recipientInfos) {
  const result = getSafe(
    () => recipientInfos.filter(info => info.mappedRecipientId === recipientId),
    null
  );
  return result ? result.map(info => info.id) : [];
}

export function isElementQualifiedSignature(element) {
  return (
    element.element === FORMELEMENTS_SIGNATURE &&
    element.signatureType === 'QUALIFIED'
  );
}

export function isDocumentNeedQualifiedSignature(
  qualifiedSignatureRequestedFrom,
  recipientId
) {
  return (
    Array.isArray(qualifiedSignatureRequestedFrom) &&
    qualifiedSignatureRequestedFrom.includes(recipientId)
  );
}

export function isElementAdvancedSignature(element) {
  return (
    element.element === FORMELEMENTS_SIGNATURE &&
    element.signatureType === 'ADVANCED'
  );
}

export function isDocumentNeedAdvancedSignature(
  formStructure,
  currentRecipientRoles
) {
  return formStructure.some(page =>
    page.some(
      item =>
        isWidgetForCurrentRecipient(
          currentRecipientRoles,
          item.recipientRole
        ) && isElementAdvancedSignature(item)
    )
  );
}

export function isWidgetForCurrentRecipient(currentRecipientRoles, widgetRole) {
  return (
    currentRecipientRoles.length === 0 ||
    currentRecipientRoles.includes(widgetRole)
  );
}

function isElementHasValueInheritance(documentId, widgetId, inheritanceInfo) {
  return getSafe(() => inheritanceInfo[documentId][widgetId]);
}

export function defineFormStructureRecipientsAndValuesInheritanceAndSpecialSignatures(
  formStructure,
  currentRecipientRoles,
  isQuickForm,
  documentId,
  _valueInheritance,
  qualifiedSigned
) {
  let qesForCurrentRecipient = false;
  let aesForCurrentRecipient = [];
  let aesForAllRecipients = [];
  let qesForAllRecipients = [];
  //let qualifiedSignedPageIndex = undefined;

  const preparedStructure =
    formStructure?.map((page, px) =>
      page.map((element, ex) => {
        const widgetIsForCurrentRecipient =
          isQuickForm ||
          isWidgetForCurrentRecipient(
            currentRecipientRoles,
            element.recipientRole
          );
        const isQesWidget = isElementQualifiedSignature(element);
        const isAesWidget = isElementAdvancedSignature(element);

        const valueInheritance =
          (widgetIsForCurrentRecipient &&
            _valueInheritance &&
            isElementHasValueInheritance(
              documentId,
              element.id,
              _valueInheritance
            )) ||
          undefined;

        if (isQesWidget) {
          const qes = {
            pageIndex: px,
            elementIndex: ex,
            ...element
          };
          if (widgetIsForCurrentRecipient) {
            qesForCurrentRecipient = qes;
          }
          qesForAllRecipients.push(qes);
        }
        if (isAesWidget) {
          const aes = {
            pageIndex: px,
            elementIndex: ex,
            ...element
          };
          aesForAllRecipients.push(aes);
          widgetIsForCurrentRecipient && aesForCurrentRecipient.push(aes);
        }
        /*if (isQesWidget && qualifiedSigned) {
          qualifiedSignedPageIndex = px;
        }*/
        return {
          ...element,
          liveKey: create_UUID(),
          valueInheritance,
          isQesWidget,
          isAesWidget,
          forCurrentRecipient: widgetIsForCurrentRecipient,
          signed: qesForCurrentRecipient && qualifiedSigned,
          readOnly:
            isFormItemNotEditable(element) || !widgetIsForCurrentRecipient
        };
      })
    ) || [];

  return {
    qesForCurrentRecipient,
    aesForCurrentRecipient,
    aesForAllRecipients,
    qesForAllRecipients,
    preparedStructure
  };
}

/*export function findAndHideWidgetsOnQualifiedSignedPage(fs) {
  return hideWidgetsOnQualifiedSignedPage(
    fs,
    fs.findIndex(p => p.some(w => isElementQualifiedSignature(w)))
  );
}

function hideWidgetsOnQualifiedSignedPage(fs, qualifiedSignedPageIndex) {
  return qualifiedSignedPageIndex !== undefined
    ? fs.map((p, i) =>
        p.map(w => ({
          ...w,
          hidden: i === qualifiedSignedPageIndex
        }))
      )
    : fs;
}*/

export function isOnlyAcceptButton(formStructure) {
  return !formStructure.some(page =>
    page.some(
      item => item.element !== FORMELEMENTS_ACCEPT_BUTTON && !item.readOnly
    )
  );
}

export function isOnlySignaturesForm(formStructure) {
  return !formStructure.some(page =>
    page.some(item => item.element !== FORMELEMENTS_SIGNATURE && !item.readOnly)
  );
}

export function isFormEditable(formStructure) {
  return formStructure.some(page =>
    page.some(item => item.forCurrentRecipient && !item.readOnly)
  );
}

export function someRequestPageNeedCompleted(requestPages) {
  return (
    requestPages && requestPages.some(page => page.pageSettings?.completeOnExit)
  );
}

export function getLastRealRequestPageIndex(requestPages) {
  // Caller contract: prepared pages with fake summary step appended last.
  return requestPages.length - 2;
}

export function canTriggerPartialSendOnPageExit(pageIndex, requestPages) {
  const page = requestPages[pageIndex];
  const lastRealPageIndex = getLastRealRequestPageIndex(requestPages);

  return (
    !page.summaryStep &&
    pageIndex !== lastRealPageIndex &&
    Boolean(page.pageSettings?.completeOnExit)
  );
}

export function prepareExternalLinks(text) {
  if (text) {
    text = text.replace(/<a/g, '<a class="link external" target="_blank"');
  }
  return text;
}

export function prepareBrandingDetails(details) {
  if (details.customFooterText) {
    details.customFooterText = prepareExternalLinks(details.customFooterText);
  }
  return details;
}

/**
 * prepareDocumentsAndPagesForCurrentState
 *
 * @param requestedDocuments
 * @param forms
 * @param scannedDocuments
 * @param documentsStatus
 * @param documentConditions
 * @param requestPages
 * @param signatures
 * @returns {{
 *      requestedDocuments: *[],
 *      requestPages: [],
 *      preparedFormsData: Map,
 *      preparedFormsStructures: Map
 *      }}
 */
export function prepareDocumentsAndPagesForCurrentState(
  requestedDocuments,
  forms,
  scannedDocuments,
  documentsStatus,
  documentConditions,
  requestPages,
  signatures,
  successSentKeys,
  rejectedDocuments
) {
  if (!requestedDocuments) {
    return {
      requestedDocuments: [],
      preparedFormsData: forms,
      requestPages: requestPages,
      preparedFormsStructures: {}
    };
  }

  //Step 1: initial preparing
  const initialRequestedDocuments = Array.from(requestedDocuments.values());
  let evalData = {
    requestedDocuments: initialRequestedDocuments,
    preparedFormsData: forms,
    preparedFormsStructures: collectFormStructures(initialRequestedDocuments)
  };

  let newEvalData;
  do {
    if (newEvalData) {
      evalData = newEvalData;
      const mergedFormsData = new Map();
      const preparedFormsData = newEvalData.preparedFormsData;
      forms.forEach((formInState, key) => {
        const preparedForm = preparedFormsData.get(key);
        formInState.forEach((valueInState, valueId) => {
          if (!preparedForm.has(valueId)) {
            preparedForm.set(valueId, valueInState);
          }
        });
        mergedFormsData.set(key, preparedForm);
      });
      evalData.preparedFormsData = mergedFormsData;
    }

    newEvalData = evaluateDocumentsCurrentState(
      initialRequestedDocuments,
      evalData.preparedFormsData,
      scannedDocuments,
      documentsStatus,
      documentConditions,
      requestPages,
      signatures,
      successSentKeys,
      rejectedDocuments
    );
  } while (
    !shallowEqual(
      newEvalData.requestedDocuments,
      evalData.requestedDocuments
    ) ||
    !shallowEqual(
      newEvalData.preparedFormsStructures,
      evalData.preparedFormsStructures
    )
  );

  //statistic
  newEvalData.requestedDocuments.forEach(doc => {
    switch (doc.type) {
      case DOC_TYPE_PDF_FORM:
      case DOC_TYPE_FORM:
        {
          const formStructure = newEvalData.preparedFormsStructures.get(doc.id);
          const formData = newEvalData.preparedFormsData.get(doc.id);
          let widgetsCount = 0;
          let filledWidgetsCount = 0;
          let requiredWidgetsCount = 0;
          let filledRequiredWidgetsCount = 0;
          formStructure.forEach(page =>
            page.forEach(widget => {
              if (!widget.static) {
                widgetsCount++;
                if (!widget.optional) requiredWidgetsCount++;
                if (formData.get(widget.id)?.length > 0) {
                  filledWidgetsCount++;
                  if (!widget.optional) {
                    filledRequiredWidgetsCount++;
                  }
                }
              }
            })
          );
          doc.stats = {
            pagesCount: formStructure.length,
            widgetsCount,
            filledWidgetsCount,
            requiredWidgetsCount,
            filledRequiredWidgetsCount: filledRequiredWidgetsCount
          };
        }
        break;
      case DOC_TYPE_DOCUMENT:
        doc.stats = {
          pages: scannedDocuments.get(doc.id)?.size || 0
        };
        break;
      case DOC_TYPE_INFORMATION:
      case DOC_TYPE_IMMEDIATE_CONFIRMATION:
        doc.stats = {
          ...documentsStatus.get(doc.id),
          downloadRequired: doc.attributes.downloadRequired
        };
        break;
      case DOC_TYPE_ID_CHECK:
        {
          const idCheckSteps = prepareIdCheckSteps(
            doc,
            scannedDocuments.get(doc.id),
            documentsStatus.get(doc.id)
          );
          doc.stats = {
            idCheckSteps: idCheckSteps.map(step => ({
              id: step.id,
              info: step.info,
              passed: step.passed
            }))
          };
        }
        break;
      default:
        doc.stats = {};
    }
    doc.stats.type = doc.type;
    doc.stats.name = doc.name;
    doc.stats.done = doc.done;
  });

  return newEvalData;
}

// window is absent in node-env unit tests.
const isPreviewMode = () =>
  typeof window !== 'undefined' && Boolean(window.previewMode);

function applyESignaturesToFormsData(signatures, documents, formsData) {
  const resultData = new Map(formsData);
  // Preview: sample QES stamp so Signature widgets render an appearance image.
  const signedMarker = isPreviewMode()
    ? DEMO_QES_APPEARANCE_DATA_URL
    : 'signed';
  documents
    .filter(
      doc =>
        doc.type === DOC_TYPE_PDF_FORM &&
        Array.isArray(doc.formStructure) &&
        resultData.get(doc.id)
    )
    .forEach(doc =>
      doc.formStructure.forEach(page =>
        page.forEach(item => {
          const isQesForRecipient =
            (item.isQesWidget || isElementQualifiedSignature(item)) &&
            item.forCurrentRecipient &&
            signatures[doc.id]?.success;
          if (item.signed || isQesForRecipient) {
            resultData.get(doc.id).set(item.id, signedMarker);
          }
        })
      )
    );
  return resultData;
}

function evaluateDocumentsCurrentState(
  _requestedDocumentsArray,
  formsData,
  scannedDocuments,
  documentsStatus,
  documentConditions,
  _requestPages,
  signatures,
  successSentKeys,
  rejectedDocuments
) {
  const requestedDocumentsArray = arrLightCopy(_requestedDocumentsArray);

  //Step 1: populate formsCommon with default data, apply conditions
  let {
    preparedFormsStructures,
    preparedFormsData
  } = applyFormConditionsAndDefaultValuesToForms(
    requestedDocumentsArray,
    applyESignaturesToFormsData(signatures, requestedDocumentsArray, formsData)
  );

  const findDocumentPage = documentId =>
    _requestPages?.find(page => page.documents?.some(id => id === documentId));

  //Step 2: apply "done" status to ready documents
  // Docs with live formData so CONDITION placeholders (e.g. email == repeat)
  // resolve inside isDocumentFormValid / WidgetValidation.
  const docsForValidation = prepareDocumentsForCaseConditions(
    requestedDocumentsArray,
    preparedFormsData
  );

  requestedDocumentsArray.forEach(document => {
    document.rejected =
      isDocumentRejected(document) || rejectedDocuments?.has(document.id);
    document.submitted =
      isDocumentSubmitted(document) || successSentKeys.has(document.id);
    document.submittedDate =
      findDocumentPage(document.id)?.submittedDate ||
      (document.submitted && timeStampToDateString(Date.now()));

    const docReady = document.submitted
      ? { done: true }
      : isDocumentReady(
          {
            ...document,
            formStructure: preparedFormsStructures.get(document.id)
          },
          scannedDocuments.get(document.id),
          preparedFormsData.get(document.id),
          documentsStatus.get(document.id),
          docsForValidation
        );

    document.done = docReady.done;
    document.ready = docReady.done;
    document.readyStatus = docReady.status;
  });

  //Step 3: apply document-conditions to all documents
  let conditionalRequestedDocumentsArray = requestedDocumentsArray;

  if (isNotEmptyArray(documentConditions)) {
    let newConditionalRequestedDocumentsArray;
    do {
      if (newConditionalRequestedDocumentsArray) {
        conditionalRequestedDocumentsArray = newConditionalRequestedDocumentsArray;
      }
      const caseConditions = new CaseConditionalLogic(
        prepareDocumentsForCaseConditions(
          conditionalRequestedDocumentsArray,
          preparedFormsData
        ),
        documentConditions
      );
      const conditionsResut = caseConditions.getConditionedDocumentList();
      newConditionalRequestedDocumentsArray = conditionsResut.documents;
    } while (
      conditionalRequestedDocumentsArray.length !==
      newConditionalRequestedDocumentsArray.length
    );
  }

  //Step 4: clear structure and data of invisible documents
  for (let k of preparedFormsStructures.keys()) {
    if (!conditionalRequestedDocumentsArray.find(doc => doc.id === k)) {
      preparedFormsStructures.delete(k);
    }
  }

  //Step 5: apply linked Values
  preparedFormsData = applyValuesInheritanceToForms(
    preparedFormsStructures,
    preparedFormsData
  );

  //Step 6: build request pages for current documents list
  const docsIds = conditionalRequestedDocumentsArray.map(doc => doc.id);
  const requestPages =
    isNotEmptyArray(_requestPages) &&
    _requestPages
      .filter(page => page.documents.some(id => docsIds.includes(id)))
      .map(page => ({
        ...page,
        documents: page.documents
          .filter(id => docsIds.includes(id))
          .map(id => requestedDocumentsArray.find(doc => doc.id === id))
      }));

  return {
    requestedDocuments: conditionalRequestedDocumentsArray,
    preparedFormsData: preparedFormsData,
    preparedFormsStructures: preparedFormsStructures,
    requestPages
  };
}

export function prepareRequestedDocuments(
  recipientId,
  requestedDocuments,
  forms,
  valueInheritance
) {
  //TODO: this function mus go on
  const preparedFormsData = new Map();
  const preparedDocuments = requestedDocuments?.reduce((result, document) => {
    if (document.attributes) {
      const {
        qualifiedSigned,
        formStructure,
        formData,
        attachments,
        smartFill,
        preHandlingDone
      } = document.attributes;

      document.attachment = attachments;

      if (formStructure) {
        document.qualifiedSigned = qualifiedSigned;
        document.finalised = qualifiedSigned || preHandlingDone;

        const {
          qesForCurrentRecipient,
          aesForCurrentRecipient,
          aesForAllRecipients,
          qesForAllRecipients,
          preparedStructure
        } = defineFormStructureRecipientsAndValuesInheritanceAndSpecialSignatures(
          formStructure,
          defineCurrentRecipientRoles(recipientId, document.recipientInfos),
          document.type === DOC_TYPE_FORM,
          document.id,
          valueInheritance,
          document.qualifiedSigned
        );

        document.formStructure = preparedStructure;
        document.needQualifiedSignature =
          !document.qualifiedSigned && qesForCurrentRecipient;
        document.curentRecipientAdvancedSignaturesList = aesForCurrentRecipient;
        document.allAdvancedSignaturesList = aesForAllRecipients;
        document.allQualifiedSignaturesList = qesForAllRecipients;

        if (smartFill && smartFill.active && smartFill.smartFillStructure) {
          document.smartFillStepsTitle = smartFill.stepTitles;
          document.smartFill = fixSmartFill(document.formStructure, smartFill);
        }

        document.isFormEditable = isFormEditable(preparedStructure);
        document.isOnlySignatures = isOnlySignaturesForm(preparedStructure);
        document.isOnlyAcceptButton = isOnlyAcceptButton(preparedStructure);

        const preparedFormData = forms.has(document.id)
          ? forms.get(document.id)
          : formData
          ? new Map(Object.entries(formData))
          : new Map();

        //Qualified signature has no data, we need populate formData with some value
        //TODO: probably need to remove, while it repeated in
        // Step 1: populate formsCommon with default data, apply conditions of evaluateDocumentsCurrentState
        if (document.qualifiedSigned && qesForCurrentRecipient?.id) {
          preparedFormData.set(
            qesForCurrentRecipient.id,
            isPreviewMode() ? DEMO_QES_APPEARANCE_DATA_URL : 'qes_signed'
          );
        }
        preparedFormsData.set(document.id, preparedFormData);
      }
    }

    result.set(document.id, document);
    return result;
  }, new Map());

  return {
    preparedDocuments,
    preparedFormsData
  };
}

function fixSmartFill(formStructure, smartFill) {
  const plainFormStructure = new Map();
  formStructure.forEach(page =>
    page.forEach(item => plainFormStructure.set(item.id, item))
  );

  const smartFillFixed = [];
  smartFill.smartFillStructure.forEach((page, pageIndex) => {
    const smartFillPage = [];
    page.forEach((smartFillItem, itemIndex) => {
      if (
        plainFormStructure.has(smartFillItem.id) ||
        smartFillItem.smartFillParagraph
      ) {
        const originalItem = smartFillItem.smartFillParagraph
          ? {
              ...smartFillItem,
              id: create_UUID(),
              element: FORMELEMENTS_PARAGRAPH,
              static: true,
              textStyle: {
                fontWeight: 'bold'
              }
            }
          : plainFormStructure.get(smartFillItem.id);

        smartFillPage.push({
          ...originalItem,
          pageIndex: pageIndex,
          elementIndex: itemIndex,
          label: smartFillItem.label ?? originalItem.label,
          options:
            smartFillItem.options &&
            smartFillItem.options.reduce((arr, smartFillOption) => {
              const originalOption = originalItem.options?.find(
                option => option.id === smartFillOption.id
              );
              if (originalOption) {
                arr.push({
                  ...smartFillOption,
                  label: smartFillOption.label ?? originalOption.label
                });
              }
              return arr;
            }, [])
        });
      }
    });
    if (smartFillPage.length) {
      smartFillFixed.push(smartFillPage);
    }
  });
  return smartFillFixed.length === 0 ? null : smartFillFixed;
}
/**
 * Using:
 *    getSafe(() => a.b.c)
 * @param func
 * @param defaultValue
 * @returns {*}
 */
export function getSafe(func, defaultValue) {
  try {
    return func();
  } catch (e) {
    if (e instanceof TypeError) {
      return defaultValue !== undefined ? defaultValue : undefined;
    } else {
      throw e;
    }
  }
}

export function timeStampToDateString(timeStamp, withTime) {
  return new Date(timeStamp).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: withTime ? '2-digit' : undefined,
    minute: withTime ? '2-digit' : undefined,
    hour12: false
  });
}
